import { z } from "zod";
import {
  timestampToDateNullable,
  serverStampDate,
} from "@/core/firebase/converters";

/**
 * Staff member record. Stored at `clubs/{clubId}/staff/{staffId}`.
 */

/**
 * Built-in staff role IDs. Custom roles may also exist in
 * `clubs/{clubId}/roles/{id}` — resolve the effective permission set via the
 * `ClubRole` document, not by comparing these strings directly.
 */
export const STAFF_ROLE = ["owner", "manager", "receptionist", "coach"] as const;
export const StaffRoleSchema = z.enum(STAFF_ROLE);
export type StaffRole = z.infer<typeof StaffRoleSchema>;

export const STAFF_ROLE_LABELS: Record<StaffRole, string> = {
  owner: "Propietario",
  manager: "Gerente",
  receptionist: "Recepcionista",
  coach: "Entrenador",
};

/**
 * All permissions known to the system. Each string is stored verbatim on
 * role documents.
 */
export const CLUB_PERMISSION = [
  "manageStaff",
  "viewStaff",
  "manageBilling",
  "editClubProfile",
  "manageFeatures",
  "manageCourts",
  "createBooking",
  "cancelBooking",
  "checkInBooking",
  "viewCrm",
  "exportData",
  "viewCalendar",
  "viewDashboard",
  "viewActivityLog",
  "deleteBooking",
  "viewAnalytics",
  "recordPayment",
  "issueRefund",
  "viewIngresos",
  "createSale",
  "refundSale",
  "manageCatalog",
] as const;
export const ClubPermissionSchema = z.enum(CLUB_PERMISSION);
export type ClubPermission = z.infer<typeof ClubPermissionSchema>;

export const CLUB_PERMISSION_LABELS: Record<ClubPermission, string> = {
  manageStaff: "Gestionar personal",
  viewStaff: "Ver personal",
  manageBilling: "Facturación",
  editClubProfile: "Editar perfil del club",
  manageFeatures: "Configuración de funciones",
  manageCourts: "Gestionar pistas",
  createBooking: "Crear / editar reservas",
  cancelBooking: "Cancelar reservas",
  checkInBooking: "Marcar llegada / ausencia",
  deleteBooking: "Eliminar reservas",
  viewCrm: "Ver clientes",
  exportData: "Exportar datos (CSV)",
  viewCalendar: "Ver calendario",
  viewDashboard: "Ver panel de control",
  viewActivityLog: "Ver registro de actividad",
  viewAnalytics: "Ver analíticas de negocio",
  recordPayment: "Cobrar reservas y ventas",
  issueRefund: "Emitir reembolsos (reservas)",
  viewIngresos: "Ver Ingresos",
  createSale: "Crear y editar ventas",
  refundSale: "Emitir reembolsos (ventas)",
  manageCatalog: "Gestionar catálogo",
};

/**
 * Permissions grouped by area for the role-permission editor. Every
 * `ClubPermission` appears in exactly one group; the type-level guard below
 * fails to compile if a new permission is added to the enum without also being
 * placed in a group, so the editor can never silently drop one.
 */
export const CLUB_PERMISSION_GROUPS = [
  {
    label: "Reservas",
    permissions: [
      "createBooking",
      "cancelBooking",
      "checkInBooking",
      "deleteBooking",
      "viewCalendar",
    ],
  },
  {
    label: "Pagos y ventas",
    permissions: [
      "recordPayment",
      "issueRefund",
      "createSale",
      "refundSale",
      "viewIngresos",
    ],
  },
  { label: "Clientes", permissions: ["viewCrm"] },
  {
    label: "Negocio",
    permissions: ["viewDashboard", "viewAnalytics", "viewActivityLog", "exportData"],
  },
  {
    label: "Club",
    permissions: ["manageCourts", "manageCatalog", "editClubProfile", "manageFeatures"],
  },
  {
    label: "Equipo y facturación",
    permissions: ["viewStaff", "manageStaff", "manageBilling"],
  },
] as const satisfies ReadonlyArray<{
  label: string;
  permissions: readonly ClubPermission[];
}>;

// Exhaustiveness guard — pure type-level, no runtime cost. `as const` above
// preserves the literal permission union, so this fails to compile if any
// ClubPermission is missing from CLUB_PERMISSION_GROUPS.
type _GroupedPermission =
  (typeof CLUB_PERMISSION_GROUPS)[number]["permissions"][number];
const _assertEveryPermissionGrouped: ClubPermission extends _GroupedPermission
  ? true
  : ["permission missing from CLUB_PERMISSION_GROUPS"] = true;
void _assertEveryPermissionGrouped;

/**
 * How the club compensates a coach. Real Spanish clubs run one — or a mix —
 * of these (see `docs/product/research/2026-07-09-trainer-role.md` §2.4):
 *
 * - `revenue_share` — a % split of the class fee. `compRate` is the fraction
 *                     (0.0–1.0, e.g. 0.6 = coach keeps 60%).
 * - `per_hour`      — flat €/hour taught. `compRate` is euros per hour.
 * - `per_class`     — flat € per class delivered. `compRate` is euros/class.
 * - `court_rental`  — the coach rents the court and keeps the fee; the club's
 *                     take is the court rental. `compRate` is the € the coach
 *                     pays the club per court slot.
 */
export const COACH_COMP_MODEL = [
  "revenue_share",
  "per_hour",
  "per_class",
  "court_rental",
] as const;
export const CoachCompModelSchema = z.enum(COACH_COMP_MODEL);
export type CoachCompModel = z.infer<typeof CoachCompModelSchema>;

export const COACH_COMP_MODEL_LABELS: Record<CoachCompModel, string> = {
  revenue_share: "% de la clase",
  per_hour: "€ por hora",
  per_class: "€ por clase",
  court_rental: "Alquiler de pista",
};

/**
 * Coach compensation profile — **capture only** in v1 (no payout engine yet;
 * that's the v2 liquidación engine, roadmap #129). Lives on the staff doc so
 * it covers both coach types: an employed coach (has a login `uid`) and a
 * freelance / court-renting coach (a staff doc with `uid: null` — never logs
 * in). Owner-writable only (staff docs are owner/admin-write); the coach never
 * sees their own comp (market norm + the `coach` role lacks `viewIngresos`).
 */
export const CoachProfileSchema = z.object({
  /** Which compensation model applies. Null until the owner sets it. */
  compModel: CoachCompModelSchema.nullable().optional(),
  /**
   * The rate — units depend on `compModel` (fraction for revenue_share, euros
   * otherwise). Null until set. Never negative.
   */
  compRate: z.number().min(0).nullable().optional(),
  /** Optional owner note (e.g. "solo clases de nivel 3+", holiday rate). */
  notes: z.string().nullable().optional(),
});
export type CoachProfile = z.infer<typeof CoachProfileSchema>;

export const StaffMemberSchema = z.object({
  id: z.string(),
  clubId: z.string().default(""),
  name: z.string().default(""),
  email: z.string().default(""),
  /**
   * The roleId string — may be a system role (e.g. 'owner') or a custom role
   * document ID. Resolve to a `ClubRole` for permission checks rather than
   * comparing this string directly.
   */
  role: z.string().default("receptionist"),
  isActive: z.boolean().default(true),
  /** Firebase Auth UID — null until the invited person signs in. */
  uid: z.string().nullable().optional(),
  invitedAt: serverStampDate,
  joinedAt: timestampToDateNullable.nullable().optional(),
  invitedByUid: z.string().nullable().optional(),
  avatarUrl: z.string().nullable().optional(),
  /** One-click invitation link token. Cleared after accept. */
  inviteToken: z.string().nullable().optional(),
  /**
   * Per-device FCM push tokens, keyed by a stable client-generated device id
   * (persisted in localStorage). One entry per browser/device the user signs
   * in on — Web Push tokens are inherently per-device. The CF
   * `_get_staff_fcm_tokens` (functions/main.py) fans out to every entry whose
   * `enabled !== false`, so an owner can silence push on the always-open
   * desktop while keeping it on their phone (Ajustes → Notificaciones).
   */
  devices: z
    .record(
      z.string(),
      z.object({
        token: z.string(),
        /** Human label for the toggle list, e.g. "Chrome · macOS". */
        label: z.string().optional(),
        /** Per-device on/off. Absent = on (default true). */
        enabled: z.boolean().default(true),
        lastSeen: z.unknown().optional(),
      }),
    )
    .optional(),
  /**
   * Set the first time this user dismisses the role-aware WelcomeDialog
   * (rendered once on first dashboard load). Per-user, not per-club, so
   * a future multi-club operator doesn't get welcomed twice. Null /
   * absent → dialog is still pending.
   */
  welcomeDialogSeenAt: timestampToDateNullable.nullable().optional(),
  /**
   * Coach compensation inputs. Present only on staff members with the `coach`
   * role (owner sets it in Personal). Absent / null for every other role.
   * Capture-only in v1 — feeds the per-coach report and, later, the v2 payout
   * engine. See `CoachProfileSchema`.
   */
  coachProfile: CoachProfileSchema.nullable().optional(),
});

export type StaffMember = z.infer<typeof StaffMemberSchema>;

/** True for the coach / Entrenador role. */
export function isCoach(s: StaffMember): boolean {
  return s.role === "coach";
}

/** True when the invited person has signed in (uid + joinedAt populated). */
export function hasStaffJoined(s: StaffMember): boolean {
  return s.uid != null && s.joinedAt != null;
}

/** Convenience: true for the owner role. */
export function isOwner(s: StaffMember): boolean {
  return s.role === "owner";
}
